import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTable } from 'react-table';
import '../../../../src/index.css'; 
import {getPermissions, putPermissions, getPermissionsList} from "../../../client/permissions/index"
import PermissionButton from '../../forms/permissionButton';
import Pagination from '../../shared/pagination';
import ContentLoader from 'react-content-loader';

const TableConfiguration = React.forwardRef((props, ref) => {
  const {searchTerm, appliedFilters} = props;

  const [users, setUsers] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modifiedUsers, setModifiedUsers] = useState([]);
  const [originalUsers, setOriginalUsers] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [noDataMessage, setNoDataMessage] = useState("");

  const containerRef = useRef(null);
  const topScrollRef = useRef(null);
  const isSyncingRef = useRef(false); 

  const handleScroll = (e) => {
    const container = e.target;
    
    if (!isSyncingRef.current && topScrollRef.current) {
      isSyncingRef.current = true;
      topScrollRef.current.scrollLeft = container.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }
  };

  const handleTopScroll = (e) => {
    const topScroll = e.target;
    
    if (!isSyncingRef.current && containerRef.current) {
      isSyncingRef.current = true;
      containerRef.current.scrollLeft = topScroll.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingRef.current = false;
      });
    }
  };

  useEffect(() => {
    if (containerRef.current && topScrollRef.current) {
      const updateTopScrollWidth = () => {
        const scrollWidth = containerRef.current.scrollWidth;
        const topScrollInner = topScrollRef.current.querySelector('.top-scroll-inner');
        if (topScrollInner) {
          topScrollInner.style.width = `${scrollWidth}px`;
        }
      };
      
      const timeoutId = setTimeout(updateTopScrollWidth, 100);
      
      window.addEventListener('resize', updateTopScrollWidth);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', updateTopScrollWidth);
      };
    }
  }, [users, loading]);

  
  useEffect(() => {
    if (appliedFilters.permissionIds && appliedFilters.permissionIds.length > 0) {
      const filtered = allPermissions.filter(permission => 
        appliedFilters.permissionIds.includes(permission.id)
      );
      setFilteredPermissions(filtered);
    } else {
      setFilteredPermissions(allPermissions);
    }
  }, [allPermissions, appliedFilters.permissionIds]);

  const TableContentLoader = ({ rows = 5, columns = allPermissions.length + 2 }) => {
    const rowHeight = 97; // Altura fila
    const headerHeight = 50; // Altura encabezado
    
    return (
      <div className="overflow-x-auto max-w-screen organigrama-container scroll-container">
        <div className='w-full'>
          <ContentLoader 
            speed={2}
            width="100%"
            height={headerHeight}
            viewBox={`0 0 1000 ${headerHeight}`}
            backgroundColor="#f3f3f3"
            foregroundColor="#ecebeb"
            className="mb-1"
          >
            {/* Columna 1: Nombre */}
            <rect 
              x="0"
              y="0"
              rx="4" 
              ry="4" 
              width="180"
              height={headerHeight}
            />

            {/* Columna 2: Correo electrónico */}
            <rect 
              x="190"
              y="0"
              rx="4" 
              ry="4" 
              width="180"
              height={headerHeight}
            />
            
            {Array(columns - 2).fill('').map((_, index) => (
              <rect 
                key={`header-${index}`}
                x={380 + (index * 150)}
                y="0"
                rx="4" 
                ry="4" 
                width="140"
                height={headerHeight}
              />
            ))}
          </ContentLoader>

          {Array(rows).fill('').map((_, rowIndex) => (
            <ContentLoader 
              key={`row-${rowIndex}`}
              speed={2}
              width="100%"
              height={rowHeight}
              viewBox={`0 0 1000 ${rowHeight}`}
              backgroundColor="#f3f3f3"
              foregroundColor="#ecebeb"
              className="mb-1"
            >
              {/* Celda 1: Nombre */}
              <rect 
                x="0"
                y="0"
                rx="0" 
                ry="0" 
                width="180"
                height={rowHeight}
                fill="#0070C0" 
              />
              {/* Celda 2: Correo electrónico */}
              <rect 
                x="190"
                y="0"
                rx="0" 
                ry="0" 
                width="180"
                height={rowHeight}
              />
              
              {Array(columns - 2).fill('').map((_, colIndex) => {
                const circleSize = 24;
                const circleX = 380 + (colIndex * 150) + 70; 
                const circleY = (rowHeight / 2) - (circleSize / 2);
                
                return (
                  <circle 
                    key={`cell-${rowIndex}-${colIndex}`}
                    cx={circleX}
                    cy={circleY}
                    r={circleSize / 2}
                  />
                );
              })}
            </ContentLoader>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const permissionsResponse = await getPermissionsList();
        setAllPermissions(permissionsResponse.data);
        
        await fetchUsers(currentPage, recordsPerPage);
        setLoading(false);
      } catch (err) {
        setError(err.message || "Error al cargar los datos");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchUsers = async (page, pageSize) => {
    try {
      const usersResponse = await getPermissions(
        page, 
        pageSize, 
        appliedFilters.userIds, 
        appliedFilters.permissionIds,
        searchTerm
      );
      
      if (usersResponse.data) {
        const { result, totalRecords, totalPages } = usersResponse.data;

        const mappedUsers = result.map(user => ({
          ...user,
          permissions: user.permissions.map(permission => ({
            id: permission.id,
            text: permission.text,
            allowed: permission.allowed,
            assigned: permission.assigned
          }))
        }));

        setUsers(mappedUsers);
        setOriginalUsers(mappedUsers);
        setTotalRecords(totalRecords);
        setTotalPages(totalPages);
        setNoDataMessage("");
        setError(null);
      }
  } catch (err) {
    if (err.response?.status === 404 && err.response?.data?.message) {
      setUsers([]);
      setNoDataMessage(err.response.data.message);
      setTotalRecords(0);
      setTotalPages(0);
      setError(null); 
    } else if (err.response?.status === 400) {
      setUsers([]);
      setNoDataMessage("No se hallaron registros.");
      setTotalRecords(0);
      setTotalPages(0);
      setError(null); 
    } else {
      setError(err.message || "Error al cargar los usuarios");
      setNoDataMessage("");
    }
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, recordsPerPage);
  }, [currentPage, recordsPerPage, appliedFilters, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  TableConfiguration.displayName = 'TableConfiguration'; 

const togglePermission = (userId, permissionId) => {
  setUsers(prevUsers => {
    return prevUsers.map(user => {
      if (user.userId === userId) {
        const permissionIndex = user.permissions.findIndex(p => p.id === permissionId);
        
        if (permissionIndex !== -1) {
          const permission = user.permissions[permissionIndex];
          
          if (permission.allowed) {
            const updatedPermissions = [...user.permissions];
            updatedPermissions[permissionIndex] = {
              ...permission,
              assigned: !permission.assigned
            };

            setModifiedUsers(prev => {
              const existingIndex = prev.findIndex(u => u.userId === userId);
              if (existingIndex >= 0) {
                return prev;
              }
              return [...prev, { userId }];
            });

            return { ...user, permissions: updatedPermissions };
          }
        }
      }
      return user;
    });
  });
};



  const columns = useMemo(() => {
  const baseColumns = [
      { 
        Header: 'Nombres y Apellidos', 
        accessor: 'name',
        style: { minWidth: '180px' },
        Cell: ({ value }) => <div className="text-center">{value}</div>
      },
      { 
        Header: 'Correo electrónico', 
        accessor: 'email',
        style: { minWidth: '180px' },
        Cell: ({ value }) => <div className="text-center">{value}</div>
      }
  ];

  const permissionColumns = filteredPermissions.map(permission => ({
      Header: permission.text,
      accessor: 'permissions',
      id: `permission-${permission.id}`,
      style: { 
        minWidth: '150px', 
      },
      Cell: ({ row }) => {
    const userPermission = row.original.permissions?.find(p => p.id === permission.id);
    const hasPermission = userPermission?.assigned || false;
    const isAllowed = userPermission?.allowed || false;
      return (

      <div className="flex items-center justify-center h-full">
        <PermissionButton 
          hasPermission={hasPermission}
          isAllowed={isAllowed}
          onClick={() => togglePermission(row.original.userId, permission.id)}
        />
      </div>
      )
    }
    }));

    return [...baseColumns, ...permissionColumns];
  }, [filteredPermissions]);

const handleSave = async () => {
  try {
    if (modifiedUsers.length > 0) {
      const requestData = modifiedUsers.map(modifiedUser => {
        const currentUser = users.find(u => u.userId === modifiedUser.userId);
        const originalUser = originalUsers.find(u => u.userId === modifiedUser.userId);
        
        return {
          userId: modifiedUser.userId,
          permissions: allPermissions.map(permission => {
            const currentPermission = currentUser?.permissions?.find(p => p.id === permission.id);
            const originalPermission = originalUser?.permissions?.find(p => p.id === permission.id);
            
            return {
              permissionId: permission.id,
              allowed: originalPermission?.allowed || false, 
              assigned: currentPermission?.assigned || false 
            };
          })
        };
      });

      
      await putPermissions(requestData);
      setModifiedUsers([]);
      
      await fetchUsers(currentPage, recordsPerPage);
      
      return true; 
    }
    return false; 
  } catch (error) {
    console.error('Error al guardar permisos:', error);
    throw error;
  }
};

  const handleCancel = () => {
    setUsers(originalUsers);
    setModifiedUsers([]);
  };
  

  React.useImperativeHandle(ref, () => ({
    handleSave,
    handleCancel,
    hasChanges: modifiedUsers.length > 0,
  }));

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
      columns,
      data: users,
  });

  const handleRecordsCountChange = (e) => {
    const newRecordsPerPage = Number(e.target.value);
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1); 
  };

  if (error) {
    return (
      <div className='items-center justify-center w-full min-h-full pt-16'>
        <p className='pt-4 text-2xl text-center text-error font-itcmedium'>
          Hubo un error al recuperar los datos.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full ">      
        {!error && users.length === 0 && noDataMessage && (
          <div className='flex items-center justify-center w-full py-28'>
            <p className='pt-4 text-xl text-center font-itcmedium'>
              {noDataMessage}
            </p>
          </div>
        )}

        {users.length > 0 && (
          <>
          <div className="flex items-center justify-between my-4">
              <div className="flex items-center space-x-2 text-sm font-itcbook">
                <p >Mostrar</p>
                <div className="border-[#D1D5DB] border-[1px] rounded-md w-[58px] flex justify-center items-center h-[40px]">
                  <select
                    className="w-full h-full px-2 text-sm bg-white rounded-md outline-none font-itcbook"
                    value={recordsPerPage}
                    onChange={handleRecordsCountChange}  
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <p >Registros</p>
              </div>
          </div>

          {loading ? (
            <TableContentLoader rows={recordsPerPage} columns={allPermissions.length + 2} />
          ) : ( 
            <>

              <div 
                ref={topScrollRef}
                className="w-full overflow-x-auto overflow-y-hidden top-scroll-container"
                onScroll={handleTopScroll}
              >
                <div 
                  className="top-scroll-inner"
                  style={{ 
                    width: containerRef.current ? `${containerRef.current.scrollWidth}px` : '100%',
                    minWidth: '100%',
                    height: '1px',
                    pointerEvents: 'none'
                  }}
                />
              </div>
            <div 
              ref={containerRef}
              onScroll={handleScroll}
              className="overflow-x-auto max-w-screen organigrama-container scroll-container">
              <div className='min-w-[1000px]'>
                  <table {...getTableProps()} className="w-full border-collapse ">
                    <thead className="bg-white">
                      {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()} className="text-center text-white text-md sm:text-lg font-itcdemi">
                          {headerGroup.headers.map(column => (
                            <th
                              {...column.getHeaderProps()}
                              style={column.style}
                              className="p-3  border bg-blue border-[#D9D9D9]"
                            >
                              {column.render('Header')}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()} >
                      {rows.map(row => {
                        prepareRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            className={`text-base  h-[87px] border-collapse text-gray font-itcmedium text-center bg-white`}
                          >
                            {row.cells.map(cell => (
                              <td
                                {...cell.getCellProps()}
                                className={`
                                  px-4 py-2 border border-[#D9D9D9] 
                                    ${cell.column.id === 'fullName' ? 'bg-blue text-white text-left' : ''}
                                `}
                              >
                                {cell.render('Cell')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
              </div>
            </div>


            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              changePage={(page) => setCurrentPage(page)}
              recordsPerPage={recordsPerPage}
              totalRecords={totalRecords}
            />
            </>
          )}
          </>
        )}
    </div>
  );
});

export default TableConfiguration;
